import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeatureProvider, useFeatures } from './context/FeatureContext';
import Layout from './components/Layout';
import RulesModal from './components/RulesModal';
import LandingPage from './pages/LandingPage';
import MarketPage from './pages/MarketPage';
import PortfolioPage from './pages/PortfolioPage';
import LeaderboardPage from './pages/LeaderboardPage';
import PlayerPage from './pages/PlayerPage';
import AuthPage from './pages/AuthPage';
import ContestsPage from './pages/ContestsPage';
import ContestDetailPage from './pages/ContestDetailPage';
import DeclarationPage from './pages/DeclarationPage';
import DeveloperPage from './pages/DeveloperPage';
import FeedPage from './pages/FeedPage';
import FriendsPage from './pages/FriendsPage';
import DonatePage from './pages/DonatePage';
import ProfilePage from './pages/ProfilePage';
import IntelligencePage from './pages/IntelligencePage';
import RulesPage from './pages/RulesPage';
import AdminPage from './pages/AdminPage';
import IplMatchPredictionTodayPage from './pages/IplMatchPredictionTodayPage';
import BestPlayersToTradePage from './pages/BestPlayersToTradePage';
import IplPlayerPerformanceAnalyticsPage from './pages/IplPlayerPerformanceAnalyticsPage';
import HowIplTradingWorksPage from './pages/HowIplTradingWorksPage';
import PostMatchAnalysisPage from './pages/PostMatchAnalysisPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (user) return <Navigate to="/market" replace />;
  return <LandingPage />;
}

function AppWithRules({ children }) {
  const { user } = useAuth();
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!sessionStorage.getItem('rules_shown')) setShowRules(true);
  }, [user]);

  return (
    <>
      {children}
      {showRules && <RulesModal onClose={() => setShowRules(false)} />}
    </>
  );
}

function AppRoutes() {
  const feat = useFeatures();

  return (
    <Routes>
      {/* Landing Page — Public entry point */}
      <Route path="/" element={<HomeRoute />} />

      {/* Public Routes */}
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/declaration" element={<DeclarationPage />} />
      <Route path="/developer" element={<DeveloperPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/ipl-match-prediction-today" element={<IplMatchPredictionTodayPage />} />
      <Route path="/best-players-to-trade-in-ipl" element={<BestPlayersToTradePage />} />
      <Route path="/ipl-player-performance-analytics" element={<IplPlayerPerformanceAnalyticsPage />} />
      <Route path="/how-ipl-trading-works" element={<HowIplTradingWorksPage />} />
      <Route path="/post-match-analysis" element={<PostMatchAnalysisPage />} />

      {/* Protected Market & Features */}
      <Route path="/market" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Market is the index — always rendered first */}
        <Route index element={feat.market ? <MarketPage /> : <Navigate to="/auth" replace />} />

        {feat.portfolio    && <Route path="portfolio"   element={<PortfolioPage />} />}
        {feat.leaderboard  && <Route path="leaderboard" element={<LeaderboardPage />} />}
        {feat.contests     && <Route path="contests"    element={<ContestsPage />} />}
        {feat.contests     && <Route path="contests/:id" element={<ContestDetailPage />} />}
        {feat.feed         && <Route path="feed"        element={<FeedPage />} />}
        {feat.friends      && <Route path="friends"     element={<FriendsPage />} />}
        {feat.intelligence && <Route path="intelligence" element={<IntelligencePage />} />}
        {feat.donate       && <Route path="donate"      element={<DonatePage />} />}
        {feat.profile      && <Route path="profile"     element={<ProfilePage />} />}
        <Route path="rules" element={<RulesPage />} />

        {/* Player detail page — always available if market is on */}
        {feat.market       && <Route path="player/:id"  element={<PlayerPage />} />}
      </Route>

      {/* Catch-all: redirect unknown routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <FeatureProvider>
        <BrowserRouter>
          <AppWithRules>
            <AppRoutes />
          </AppWithRules>
        </BrowserRouter>
      </FeatureProvider>
    </AuthProvider>
  );
}
